import { Landmark, Church, Mountain, Castle, Clock, MapPin, CloudSunRain, Umbrella, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Chapter";
import { RAINY_IDEAS, BOOKING_URL } from "../lib/data";

const ICONS = { landmark: Landmark, church: Church, cave: Mountain, castle: Castle };

export default function PluiePage() {
  return (
    <main data-testid="page-pluie" className="grain">
      <section className="relative pt-40 pb-14 px-6 sm:px-10 lg:px-16 bg-cream overflow-hidden">
        <Umbrella className="absolute -right-8 top-20 w-64 h-64 text-pine/5 -rotate-12" />
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-body text-xs font-bold tracking-[0.25em] uppercase text-terra">
              Le plan B des campeurs malins
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-4xl">
              Que faire en Aveyron <span className="text-pine">quand il pleut ?</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-ink/65 text-base lg:text-lg leading-relaxed">
              Pas de panique si la météo se gâte ! Voici 4 idées concrètes pour ne pas
              rester sous la tente — toutes à moins de 40 minutes de Martiel, et toutes à l'abri.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="pluie-badges">
              {["4 idées à l'abri", "moins de 40 min de route", "familles & couples"].map((b) => (
                <span key={b} className="rounded-full px-4 py-2 text-xs font-bold bg-sand border border-ink/10 text-ink/60">
                  {b}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 bg-cream">
        <div className="max-w-5xl mx-auto space-y-6">
          {RAINY_IDEAS.map((idea, i) => {
            const Icon = ICONS[idea.icon];
            return (
              <Reveal key={idea.name} delay={0.06 * i}>
                <article
                  className="group rounded-[1.75rem] bg-sand border border-ink/10 p-6 sm:p-8 transition-all duration-300 hover:border-pine/40 hover:shadow-xl hover:shadow-pine/10 hover:-translate-y-0.5"
                  data-testid={`pluie-card-${i}`}
                >
                  <div className="flex items-start gap-5">
                    <span className="font-display font-black text-4xl sm:text-5xl text-terra/80 leading-none shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="grid place-items-center w-11 h-11 rounded-full bg-pine text-sand transition-transform duration-300 group-hover:-rotate-12">
                          <Icon size={19} />
                        </span>
                        <div>
                          <h2 className="font-display font-bold text-2xl text-ink leading-tight">{idea.name}</h2>
                          <div className="text-sm text-ink/50 font-medium">{idea.place}</div>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-ink/55 font-medium">
                        <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-pine" /> {idea.distance} de Martiel</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-pine" /> {idea.duration}</span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/25 text-pinedark px-3 py-0.5 text-xs font-bold">
                          <CloudSunRain size={13} /> 100 % à l'abri
                        </span>
                      </div>
                      <p className="mt-4 text-sm sm:text-base text-ink/65 leading-relaxed">{idea.desc}</p>
                      <p className="mt-2 text-xs text-ink/45">
                        <b className="text-ink/60">Idéal :</b> {idea.ideal}
                      </p>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        <div className="max-w-5xl mx-auto mt-12">
          <Reveal delay={0.2}>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pluie-cta-booking"
              className="flex items-center justify-between gap-4 rounded-[1.75rem] bg-pine text-sand p-6 sm:p-8 transition-all duration-300 hover:bg-pinedark hover:-translate-y-1 hover:shadow-xl hover:shadow-pine/25"
            >
              <div>
                <div className="font-display font-bold text-2xl sm:text-3xl">Pluie ou soleil, on vous attend.</div>
                <p className="mt-1.5 text-sm sm:text-base text-sand/70">
                  Et s'il pleut vraiment trop : la piscine couverte et chauffée du camping reste ouverte.
                </p>
              </div>
              <span className="grid place-items-center w-12 h-12 shrink-0 rounded-full bg-gold text-pinedark">
                <ArrowUpRight size={20} />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-8 text-center font-hand text-3xl text-terra -rotate-1">
              la pluie en Aveyron, c'est juste le lac qui grossit…
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
